import React, {useState, useCallback, useEffect, useMemo} from "react";
import {
  useUserOperations,
  useCharactersByPlayer,
  useActiveUsers,
  useCharactersRealtime,
} from "../../hooks/useFirestore";
import {usePagination} from "../../hooks/usePagination";
import {useAuth} from "../../AuthContext";
import AuthService, {UserRole} from "../../services/firebase/auth";
import ConfirmDeleteDialog from "../../components/ConfirmDeleteDialog";
import {formatJoinDate} from "../../utils/dateUtils";
import {adminUpdateCharacter} from "../../services/api/charactersService";
import {useNotifications} from "../../components/NotificationSystem";
import FilterableList from "../../components/FilterableList";
import {BaseFilters} from "../../components/ListFilters";
import {Box, FormControl, InputLabel, MenuItem, Select} from "@mui/material";

// User interface for the table
interface User {
  uid: string;
  username: string;
  email: string;
  roles: UserRole[];
  lastActive: string;
  joinDate: string;
  dmTokens?: number;
}

const roleOptions: {value: UserRole; label: string}[] = [
  {value: "admin", label: "Admin"},
  {value: "moderator", label: "Moderator"},
  {value: "dm", label: "DM"},
  {value: "player", label: "Player"},
];

// Sort order helper removed in favor of simple username sort within page

// Helper to truncate text and show full value on hover
function truncateWithTooltip(text: string, maxLength: number) {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "…";
}

type UserFilters = BaseFilters & {
  role: "all" | UserRole;
  active: "all" | "active" | "inactive";
};

const DEFAULT_FILTERS: UserFilters = {
  search: "",
  role: "all",
  active: "all",
};

const UserManagement: React.FC = () => {
  const {updateUser} = useUserOperations();
  const {user: currentUser} = useAuth();
  const {addNotification} = useNotifications();
  const {data: activeUsers} = useActiveUsers();
  const {data: allCharacters} = useCharactersRealtime();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [updatingRoles, setUpdatingRoles] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [filters, setFilters] = useState<UserFilters>(DEFAULT_FILTERS);

  // Character expansion state
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);

  // Server-side filters for pagination
  const serverFilters = useMemo(() => {
    const f: any[] = [];
    if (filters.role !== "all") {
      f.push({field: "roles", operator: "array-contains", value: filters.role});
    }
    if (filters.active === "active") {
      f.push({field: "isActive", operator: "==", value: true});
    } else if (filters.active === "inactive") {
      f.push({field: "isActive", operator: "==", value: false});
    }
    return f;
  }, [filters.role, filters.active]);

  // Infinite pagination over users
  const {
    data: pagedUsers,
    loading,
    error,
    hasMore,
    loadMore,
    reset,
  } = usePagination<any>(
    "users",
    {pageSize: 30, orderByField: "joinDate", orderDirection: "desc"},
    serverFilters,
    // Use Firestore prefix search on displayName when searching
    "displayName",
    filters.search
  );

  const mappedUsers: User[] = useMemo(
    () =>
      (pagedUsers || []).map((user: any) => ({
        uid: user.uid ?? user.id ?? "",
        username: user.displayName,
        email: user.email,
        roles: (user.roles || ["player"]) as UserRole[],
        lastActive: formatJoinDate(user.lastActive),
        joinDate: formatJoinDate(user.joinDate),
        dmTokens: user.dmTokens,
      })),
    [pagedUsers]
  );

  // Maintain a stable display order within the current page
  const sortedUsers = useMemo(() => {
    return [...mappedUsers].sort((a, b) =>
      a.username.localeCompare(b.username)
    );
  }, [mappedUsers]);

  // Active counts
  const activeUsersCount = activeUsers?.length ?? 0;
  const activeCharactersCount = useMemo(() => {
    if (!allCharacters) return 0;
    return allCharacters.filter(
      (c: any) => c?.isActive && c?.status === "active"
    ).length;
  }, [allCharacters]);

  const handleRoleToggle = useCallback(
    async (uid: string, role: UserRole) => {
      if (!currentUser) return;

      try {
        setUpdatingRoles(uid);

        // Replace roles with the new role instead of adding/removing
        // Users should only have one role at a time
        const newRoles = [role];

        // Update in Firestore
        await updateUser(uid, {roles: newRoles});

        // Update custom claims via Cloud Function
        await AuthService.setUserRole(uid, newRoles);

        // Refresh the list
        reset();

        addNotification({
          type: "success",
          title: "Role Updated",
          message: `User role has been updated successfully.`,
          duration: 4000,
        });
      } catch (error) {
        addNotification({
          type: "error",
          title: "Role Update Failed",
          message:
            error instanceof Error
              ? error.message
              : "Failed to update user role. Please try again.",
          duration: 6000,
        });
      } finally {
        setUpdatingRoles(null);
      }
    },
    [currentUser, updateUser, reset]
  );

  const handleRemove = (uid: string) => {
    setDeleteError(null);
    setDeleteDialogOpen(true);
    setPendingDeleteId(uid);
  };

  const handleConfirmDelete = useCallback(async () => {
    if (pendingDeleteId) {
      try {
        setDeletingUser(pendingDeleteId);
        setDeleteError(null);

        // Use AuthService to delete from both Auth and Firestore
        await AuthService.deleteUser(pendingDeleteId);

        // Refresh the user list to show updated data
        reset();

        addNotification({
          type: "success",
          title: "User Deleted",
          message: "User has been deleted successfully.",
          duration: 4000,
        });
      } catch (error: any) {
        const errorMessage = error.message || "Failed to delete user";
        setDeleteError(errorMessage);
        addNotification({
          type: "error",
          title: "Delete Failed",
          message: errorMessage,
          duration: 6000,
        });
      } finally {
        setDeletingUser(null);
      }
    }
    setDeleteDialogOpen(false);
    setPendingDeleteId(null);
  }, [pendingDeleteId, reset]);

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setPendingDeleteId(null);
    setDeleteError(null);
  };

  // Character management handlers
  const handleToggleUserCharacters = (uid: string) => {
    setExpandedUserId(expandedUserId === uid ? null : uid);
  };

  // Component to render user's characters
  const UserCharactersDropdown: React.FC<{uid: string}> = ({uid}) => {
    // Use non-real-time hook to prevent constant refreshing during editing
    const {
      data: characters,
      loading: charactersLoading,
      error: charactersError,
    } = useCharactersByPlayer(uid);
    const [localCharacters, setLocalCharacters] = useState<typeof characters>(
      []
    );
    const [hasLocalChanges, setHasLocalChanges] = useState(false);
    const [characterUpdates, setCharacterUpdates] = useState<
      Record<
        string,
        {level?: number; mementoTokens?: number; downtimeDays?: number}
      >
    >({});

    // Update local characters when data changes from server
    useEffect(() => {
      if (characters && !hasLocalChanges) {
        setLocalCharacters(characters);
      }
    }, [characters, hasLocalChanges]);

    // Character update handlers
    const handleSaveCharacter = async (characterId: string) => {
      if (!characterUpdates[characterId]) return;

      try {
        await adminUpdateCharacter({
          characterId,
          updates: characterUpdates[characterId],
        });

        addNotification({
          type: "success",
          title: "Character Updated",
          message: "Character has been updated successfully.",
          duration: 4000,
        });
        setCharacterUpdates({});
        setHasLocalChanges(false);
        setLocalCharacters(characters);
      } catch (error) {
        addNotification({
          type: "error",
          title: "Update Failed",
          message:
            error instanceof Error
              ? error.message
              : "Failed to update character. Please try again.",
          duration: 6000,
        });
      }
    };

    const handleCharacterValueChange = (
      characterId: string,
      field: "level" | "mementoTokens" | "downtimeDays",
      value: string
    ) => {
      // Handle empty string case properly
      const numericValue = value === "" ? 0 : parseInt(value) || 0;

      setCharacterUpdates({
        ...characterUpdates,
        [characterId]: {
          ...characterUpdates[characterId],
          [field]: numericValue,
        },
      });
    };

    if (charactersLoading) {
      return (
        <div style={{padding: "16px", color: "#666"}}>
          Loading characters...
        </div>
      );
    }

    if (charactersError) {
      return (
        <div style={{padding: "16px", color: "#d63031"}}>
          Error loading characters: {charactersError}
        </div>
      );
    }

    if (!localCharacters || localCharacters.length === 0) {
      return (
        <div style={{padding: "16px", color: "#666", fontStyle: "italic"}}>
          No characters found
        </div>
      );
    }

    return (
      <div
        style={{
          borderTop: "1px solid #eee",
          backgroundColor: "#f9f9f9",
        }}
      >
        <div style={{display: "flex", flexDirection: "column"}}>
          {localCharacters.map((character) => {
            const currentUpdates = characterUpdates[character.id || ""] || {};

            return (
              <div
                key={character.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  backgroundColor: "#fff",
                  borderBottom: "1px solid #e0e0e0",
                  fontSize: "0.875rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    flex: 1,
                  }}
                >
                  <div
                    style={{fontWeight: 600, color: "#333", minWidth: "120px"}}
                  >
                    {character.name}
                  </div>
                  <div style={{color: "#666", minWidth: "100px"}}>
                    {character.species} {character.class}
                  </div>
                  <div
                    style={{display: "flex", alignItems: "center", gap: "12px"}}
                  >
                    <span>
                      Level:{" "}
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={
                          currentUpdates.level !== undefined
                            ? currentUpdates.level === 0
                              ? ""
                              : currentUpdates.level
                            : character.level
                        }
                        onChange={(e) => {
                          handleCharacterValueChange(
                            character.id || "",
                            "level",
                            e.target.value
                          );
                          setHasLocalChanges(true);
                        }}
                        style={{
                          width: "50px",
                          padding: "2px 4px",
                          border: "1px solid #ccc",
                          borderRadius: "3px",
                          fontSize: "0.875rem",
                        }}
                      />
                    </span>
                    <span>
                      Tokens:{" "}
                      <input
                        type="number"
                        min="0"
                        value={
                          currentUpdates.mementoTokens !== undefined
                            ? currentUpdates.mementoTokens === 0
                              ? ""
                              : currentUpdates.mementoTokens
                            : character.mementoTokens
                        }
                        onChange={(e) => {
                          handleCharacterValueChange(
                            character.id || "",
                            "mementoTokens",
                            e.target.value
                          );
                          setHasLocalChanges(true);
                        }}
                        style={{
                          width: "50px",
                          padding: "2px 4px",
                          border: "1px solid #ccc",
                          borderRadius: "3px",
                          fontSize: "0.875rem",
                        }}
                      />
                    </span>
                    <span>
                      Downtime:{" "}
                      <input
                        type="number"
                        min="0"
                        value={
                          currentUpdates.downtimeDays !== undefined
                            ? currentUpdates.downtimeDays === 0
                              ? ""
                              : currentUpdates.downtimeDays
                            : character.downtimeDays
                        }
                        onChange={(e) => {
                          handleCharacterValueChange(
                            character.id || "",
                            "downtimeDays",
                            e.target.value
                          );
                          setHasLocalChanges(true);
                        }}
                        style={{
                          width: "50px",
                          padding: "2px 4px",
                          border: "1px solid #ccc",
                          borderRadius: "3px",
                          fontSize: "0.875rem",
                        }}
                      />
                    </span>
                  </div>
                </div>
                <div style={{display: "flex", gap: "8px"}}>
                  <button
                    className="btn btn-primary"
                    style={{fontSize: "0.75rem", padding: "4px 8px"}}
                    onClick={() => handleSaveCharacter(character.id || "")}
                  >
                    Save
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Component to render DM token editing for DMs
  const DMTokenEditor: React.FC<{user: User}> = ({user}) => {
    const [dmTokenUpdate, setDmTokenUpdate] = useState<number | undefined>(
      undefined
    );
    const [updatingTokens, setUpdatingTokens] = useState(false);

    const handleSaveDMTokens = async () => {
      if (dmTokenUpdate === undefined) return;

      try {
        setUpdatingTokens(true);
        await updateUser(user.uid, {dmTokens: dmTokenUpdate});

        // Refresh user list to reflect new DM token values
        reset();

        addNotification({
          type: "success",
          title: "DM Tokens Updated",
          message: `DM tokens for ${user.username} have been updated successfully.`,
          duration: 4000,
        });
        setDmTokenUpdate(undefined);
      } catch (error) {
        addNotification({
          type: "error",
          title: "Update Failed",
          message:
            error instanceof Error
              ? error.message
              : "Failed to update DM tokens. Please try again.",
          duration: 6000,
        });
      } finally {
        setUpdatingTokens(false);
      }
    };

    const handleDmTokenValueChange = (value: string) => {
      // Handle empty string case properly
      const numericValue = value === "" ? 0 : parseInt(value) || 0;
      setDmTokenUpdate(numericValue);
    };

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          backgroundColor: "#fff",
          borderBottom: "1px solid #e0e0e0",
          fontSize: "0.875rem",
        }}
      >
        <div
          style={{display: "flex", alignItems: "center", gap: "16px", flex: 1}}
        >
          <div style={{fontWeight: 600, color: "#333", minWidth: "120px"}}>
            DM Tokens
          </div>
          <div style={{display: "flex", alignItems: "center", gap: "12px"}}>
            <span>
              Tokens:{" "}
              <input
                type="number"
                min="0"
                value={
                  dmTokenUpdate !== undefined
                    ? dmTokenUpdate === 0
                      ? ""
                      : dmTokenUpdate
                    : user.dmTokens === 0
                      ? ""
                      : user.dmTokens
                }
                onChange={(e) => handleDmTokenValueChange(e.target.value)}
                style={{
                  width: "50px",
                  padding: "2px 4px",
                  border: "1px solid #ccc",
                  borderRadius: "3px",
                  fontSize: "0.875rem",
                }}
              />
            </span>
          </div>
        </div>
        <div style={{display: "flex", gap: "8px"}}>
          <button
            className="btn btn-primary"
            style={{fontSize: "0.75rem", padding: "4px 8px"}}
            onClick={handleSaveDMTokens}
            disabled={updatingTokens || dmTokenUpdate === undefined}
          >
            {updatingTokens ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="env-page-container">
      <h2 className="env-page-title">User management</h2>
      <section className="env-section">
        <div className="env-section-header">
          <div />
          <div
            style={{
              display: "flex",
              gap: 16,
              alignItems: "center",
              color: "#666",
              fontSize: 14,
            }}
          >
            <span>
              <strong>Active Users:</strong> {activeUsersCount}
            </span>
            <span>
              <strong>Active Characters:</strong> {activeCharactersCount}
            </span>
          </div>
        </div>
        <div style={{height: "70vh"}}>
          <FilterableList
            data={sortedUsers}
            loading={loading}
            error={error}
            filters={filters}
            onFiltersChange={setFilters}
            onClearFilters={() => setFilters(DEFAULT_FILTERS)}
            searchFields={["username", "email"] as any}
            searchPlaceholder="Search users by name or email..."
            emptyMessage="No users found matching your criteria"
            useInfiniteScroll
            hasMore={hasMore}
            onLoadMore={loadMore}
            renderItem={(user) => (
              <div
                className="env-user-table-border"
                style={{background: "#fff", borderRadius: 6}}
              >
                <div
                  className="env-user-row"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 2fr 3fr 1fr 1fr auto",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                  }}
                >
                  {/* Username */}
                  <div
                    title={user.username}
                    style={{cursor: "pointer"}}
                    onClick={() => handleToggleUserCharacters(user.uid)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <button
                        className="btn env-btn-link"
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                          fontSize: 12,
                        }}
                        aria-label={
                          expandedUserId === user.uid ? "Collapse" : "Expand"
                        }
                      >
                        {expandedUserId === user.uid ? "▾" : "▸"}
                      </button>
                      {truncateWithTooltip(user.username, 14)}
                    </div>
                  </div>

                  {/* Email */}
                  <div title={user.email} className="truncate-cell email-cell">
                    {truncateWithTooltip(user.email, 24)}
                  </div>

                  {/* Roles */}
                  <div>
                    <div className="env-user-roles-container">
                      {roleOptions.map((roleOption) => (
                        <label
                          key={roleOption.value}
                          className="env-user-role-checkbox"
                        >
                          <input
                            type="radio"
                            name={`role-${user.uid}`}
                            checked={user.roles.includes(roleOption.value)}
                            onChange={() =>
                              handleRoleToggle(user.uid, roleOption.value)
                            }
                            disabled={updatingRoles === user.uid}
                            aria-label={`Set ${roleOption.label} role for ${user.username}`}
                            className="env-user-role-checkbox-input"
                          />
                          <span className="env-user-role-label">
                            {roleOption.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Last active */}
                  <div className="date-cell">{user.lastActive}</div>

                  {/* Join date */}
                  <div className="date-cell">{user.joinDate}</div>

                  {/* Actions */}
                  <div style={{justifySelf: "end"}}>
                    <button
                      className="env-user-remove-btn"
                      onClick={() => handleRemove(user.uid)}
                      disabled={
                        pendingDeleteId === user.uid ||
                        deletingUser === user.uid
                      }
                      aria-label={`Remove ${user.username}`}
                    >
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 7V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V7M4 7H16M9 10V13M11 10V13M8 7V5C8 4.44772 8.44772 4 9 4H11C11.5523 4 12 4.44772 12 5V7"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                {expandedUserId === user.uid && (
                  <div style={{padding: 0, borderTop: "1px solid #eee"}}>
                    <UserCharactersDropdown uid={user.uid} />
                    {(user.roles.includes("dm") ||
                      user.roles.includes("admin") ||
                      user.roles.includes("moderator")) && (
                      <DMTokenEditor user={user} />
                    )}
                  </div>
                )}
              </div>
            )}
          >
            {/* Advanced filters */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {/* Role Filter */}
              <FormControl sx={{minWidth: 160}}>
                <InputLabel id="role-filter-label">Role</InputLabel>
                <Select
                  labelId="role-filter-label"
                  value={filters.role}
                  label="Role"
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      role: e.target.value as UserFilters["role"],
                    })
                  }
                  size="small"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="moderator">Moderator</MenuItem>
                  <MenuItem value="dm">DM</MenuItem>
                  <MenuItem value="player">Player</MenuItem>
                </Select>
              </FormControl>

              {/* Active Filter */}
              <FormControl sx={{minWidth: 160}}>
                <InputLabel id="active-filter-label">Status</InputLabel>
                <Select
                  labelId="active-filter-label"
                  value={filters.active}
                  label="Status"
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      active: e.target.value as UserFilters["active"],
                    })
                  }
                  size="small"
                >
                  <MenuItem value="all">All Users</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </FilterableList>
        </div>
      </section>
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        message="Are you sure you want to delete this user? This action cannot be undone and will remove the user from both the system and their authentication account."
        error={deleteError}
      />
    </div>
  );
};

export default UserManagement;

// CSS classes used (add to env.css if not present):
// .env-user-roles-container
// .env-user-role-checkbox
// .env-user-role-checkbox-input
// .env-user-role-label
// .env-user-roles-display
// .env-loading-state
// .env-error-state
